// ============================================================================
// WizardContainerInner Component (View)
// ============================================================================

import { Box, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Chip } from "@mui/material";
import { Close } from "@mui/icons-material";
import { StepProgress } from "../components/StepProgress";
import { WizardNavigation } from "../components/WizardNavigation";
// Configuration phase steps
import FileValidation from "../steps/FileValidation";
import PropertyMapping from "../steps/PropertyMapping";
import GeoTypeSelection from "../steps/GeoTypeSelection";
// Validation phase steps
import { OverlappingBoundaries } from "../steps/OverlappingBoundaries";
import { DuplicateFeatures } from "../steps/DuplicateFeatures";
import { UnmatchedFeatures } from "../steps/UnmatchedFeatures";
import { MissingInvalidData } from "../steps/MissingInvalidData";
import { Summary } from "../steps";
import type { WizardContainerInnerProps } from "./WizardContainerInner.types";
import { useWizardContainerInnerViewModel } from "./WizardContainerInner.vm";
import "./WizardContainerInner.css";

/**
 * Inner container component that uses wizard context
 */
export function WizardContainerInner(props: WizardContainerInnerProps) {
	const { onCancel, onStartOver, company, allDeals, salespersons, branches, growers, user } = props;
	const vm = useWizardContainerInnerViewModel(props);

	// Render current step content
	const renderStepContent = () => {
		switch (vm.currentStep) {
			// Configuration phase steps
			case "file-validation":
				return (
					<FileValidation
						warnings={vm.configuration.warnings}
						uploadedFiles={vm.configuration.uploadedFiles}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleNext}
						onAddFiles={vm.handleAddFiles}
						onClear={vm.handleClearFiles}
					/>
				);
			case "property-mapping":
				return (
					<PropertyMapping
						geodataGroups={vm.configuration.geodataGroups}
						propertyMappings={vm.configuration.propertyMappings}
						onMappingChange={vm.handleMappingChange}
						onBatchMappingChange={vm.handleBatchMappingChange}
						onApplyGlobalMapping={vm.handleApplyGlobalMapping}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleNext}
						company={company}
					/>
				);
			case "geo-type-selection":
				return (
					<GeoTypeSelection
						geodataGroups={vm.configuration.geodataGroups}
						geoTypeSelections={vm.configuration.geoTypeSelections}
						excludedGroupIds={vm.configuration.excludedGroupIds}
						selectedGroupIdsForMap={vm.configuration.selectedGroupIdsForMap}
						propertyMappings={vm.configuration.propertyMappings}
						configuration={vm.configuration}
						onGeoTypeChange={vm.handleGeoTypeChange}
						onApplyToAll={vm.handleApplyToAllGeoType}
						onApplyToSelected={vm.handleApplyToSelectedGeoType}
						onExcludeGroup={vm.handleExcludeGroup}
						onIncludeGroup={vm.handleIncludeGroup}
						onMapSelectionChange={vm.handleMapSelectionChange}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleCompleteConfiguration}
						company={company}
						allDeals={allDeals}
						salespersons={salespersons}
						branches={branches}
						growers={growers}
						user={user}
					/>
				);
			// Validation phase steps
			case "overlapping-boundaries":
				return (
					<OverlappingBoundaries
						issues={vm.issues.overlaps}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
					/>
				);
			case "duplicate-features":
				return (
					<DuplicateFeatures
						issues={vm.issues.duplicates}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
						onDeleteJobs={vm.handleDeleteJobs}
					/>
				);
			case "unmatched-features":
				return (
					<UnmatchedFeatures
						issues={vm.issues.unmatched}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
						onAssignToJob={vm.handleAssignToJob}
						onSplitFeatures={vm.handleSplitFeatures}
						onUploadBoundary={vm.handleUploadBoundary}
					/>
				);
			case "missing-invalid-data":
				return (
					<MissingInvalidData
						issues={vm.issues.invalidData}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onUpdateJob={vm.handleUpdateJob}
						onDeleteJob={vm.handleDeleteJob}
						onRegisterActions={vm.setInvalidDataActions}
					/>
				);
			case "summary":
				return <Summary jobs={vm.jobs} issues={vm.issues} skippedIssueIds={vm.skippedIssueIds} />;
			default:
				return null;
		}
	};

	return (
		<div className="wizard-container-inner">
			{/* Header with Step Progress */}
			<DialogTitle className="dialog-title">
				<StepProgress
					currentStep={vm.currentStep}
					visibleSteps={vm.visibleSteps}
					completedSteps={vm.completedSteps}
					resolvedSteps={vm.resolvedSteps}
					stepIssueCounts={vm.stepIssueCounts}
				/>
				{vm.totalJobCount > 0 && (
					<Chip
						label={`${vm.totalJobCount} ${vm.totalJobCount === 1 ? "Job" : "Jobs"}`}
						size="small"
						color="primary"
						variant="outlined"
						className="jobs-chip"
					/>
				)}
				<IconButton aria-label="close" onClick={onCancel} className="close-button">
					<Close />
				</IconButton>
			</DialogTitle>

			{/* Content */}
			<DialogContent dividers className="dialog-content">
				{vm.isValidating ? (
					<Box className="validating-state">
						<Typography>Validating jobs...</Typography>
					</Box>
				) : vm.validationError ? (
					<Box className="error-state">
						<Typography>Error: {vm.validationError}</Typography>
					</Box>
				) : (
					renderStepContent()
				)}
			</DialogContent>

			{/* Navigation */}
			<DialogActions className="dialog-actions">
				<WizardNavigation
					isFirstStep={vm.isFirstStep}
					isLastStep={vm.isLastStep}
					canProceed={vm.canClickNext}
					canClickPrevious={vm.canClickPrevious}
					disabledReason={vm.disabledReason}
					onPrevious={vm.handlePrevious}
					onNext={vm.handleNext}
					onFinish={vm.handleFinish}
					// Issue props (for Skip buttons)
					issueCount={vm.currentStepIssues.length}
					unskippedCount={vm.unskippedCount}
					isCurrentIssueSkipped={vm.isCurrentIssueSkipped}
					onSkipIssue={vm.currentIssue ? vm.handleSkipIssue : undefined}
					onSkipAllIssues={vm.handleSkipAllIssues}
					// MissingInvalidData step actions
					onDeleteJob={vm.currentStep === "missing-invalid-data" ? vm.invalidDataActions?.requestDelete : undefined}
					onSaveChanges={vm.currentStep === "missing-invalid-data" ? vm.invalidDataActions?.requestSave : undefined}
					// Start Over button (only shown when onStartOver prop is provided)
					onStartOver={onStartOver ? vm.handleStartOver : undefined}
					// GeoTypeSelection status chips
					geoTypeStatus={vm.geoTypeStatus}
				/>
			</DialogActions>
		</div>
	);
}
